/**
 * Flavor Cards Addon for Dawn Theme
 * Version: 1.0.0
 * Standalone JavaScript - No conflicts with Dawn theme
 */

(function() {
    'use strict';
  
    class FlavorCardsAddon {
      constructor(container) {
        this.container = container;
        this.cards = container.querySelectorAll('.flavor-card-addon');
        this.quantityInputs = container.querySelectorAll('.flavor-card-addon__quantity-input');
        this.quantityMeter = container.querySelector('[data-quantity-meter-addon]');
        
        this.selectedVariants = new Map();
        
        // En Shopify, le flavor-cards peut être une section séparée du formulaire
        // On cherche le formulaire produit principal (pas dans un drawer)
        let formCandidate = container.closest('form[action*="/cart/add"]');
        
        if (!formCandidate) {
          formCandidate = container.parentElement?.querySelector('form[action*="/cart/add"]');
        }
        
        if (!formCandidate) {
          const allForms = document.querySelectorAll('form[action*="/cart/add"]');
          
          for (const form of allForms) {
            const isInDrawer = form.closest('cart-drawer') || 
                               form.closest('#CartDrawer') ||
                               form.classList.contains('upsell-drawer-form');
            
            if (!isInDrawer) {
              formCandidate = form;
              break;
            }
          }
        }
        
        // Rejeter les formulaires upsell du cart drawer
        if (formCandidate && (
            formCandidate.closest('cart-drawer') || 
            formCandidate.closest('#CartDrawer') ||
            formCandidate.classList.contains('upsell-drawer-form')
        )) {
          this.productForm = null;
        } else {
          this.productForm = formCandidate;
        }
        
        this.displayMode = container.dataset.displayMode || 'variants';

        // Configuration par défaut des paliers
        this.tiers = [
          { qty: 1, text: '1 bottle', price: '$29.99/ea' },
          { qty: 2, text: '2 bottles', price: '$28.99/ea' },
          { qty: 3, text: '3 bottles', price: '$27.99/ea' },
          { qty: 4, text: '4+ bottles', price: '$26.99/ea' }
        ];

        // Configuration par défaut des cadeaux
        this.gifts = [
          { threshold: 3, text: 'Échantillon gratuit' },
          { threshold: 5, text: 'Livraison gratuite' }
        ];

        // Configuration des discounts par palier (sur le total)
        this.discounts = {
          '1': { type: 'none', value: 0 },
          '2': { type: 'none', value: 0 },
          '3': { type: 'none', value: 0 },
          '4': { type: 'none', value: 0 },
          '5': { type: 'none', value: 0 }
        };

        // Configuration du palier 5 (caché)
        this.tier5Enabled = false;
        this.tier5Qty = 5;

        this.init();
      }
  
      init() {
        this.loadConfiguration();
        this.restoreFromLocalStorage(); // Restaurer les sélections sauvegardées
        this.bindEvents();
        this.initializeDefaultQuantities();
        this.initializeQuantityButtons();
        this.initializeAddButtons();
        this.updateQuantityMeter();
        this.updateProductForm();
        this.interceptFetchRequests();
        this.initializeScrollIndicator();

        // Écouter quand ButtonPriceManager est prêt
        document.addEventListener('buttonPriceManagerReady', () => {
          if (this.selectedVariants.size > 0) {
            this.updateButtonPrice();
          }
        });
      }
  
      initializeDefaultQuantities() {
        // Initialiser les quantités par défaut (pour le produit actuel notamment)
        this.quantityInputs.forEach(input => {
          const defaultQuantity = parseInt(input.dataset.defaultQuantity) || 0;
          const currentValue = parseInt(input.value) || 0;

          if (defaultQuantity > 0 && currentValue > 0) {
            // Simuler l'ajout de cette quantité à la sélection
            this.handleQuantityChangeDirectly(input, currentValue);
          }
        });
      }

      saveToLocalStorage() {
        const storageKey = 'flavor_selections_' + window.location.pathname;
        const selections = {};

        this.selectedVariants.forEach((data, variantId) => {
          selections[variantId] = data;
        });

        try {
          localStorage.setItem(storageKey, JSON.stringify(selections));
        } catch (e) {
          console.error('Erreur sauvegarde localStorage:', e);
        }
      }

      clearLocalStorage() {
        const storageKey = 'flavor_selections_' + window.location.pathname;
        try {
          localStorage.removeItem(storageKey);
        } catch (e) {
          console.error('Erreur suppression localStorage:', e);
        }
      }

      restoreFromLocalStorage() {
        const storageKey = 'flavor_selections_' + window.location.pathname;

        try {
          const saved = localStorage.getItem(storageKey);
          if (!saved) {
            return;
          }

          const selections = JSON.parse(saved);

          // Restaurer dans la Map
          Object.entries(selections).forEach(([variantId, data]) => {
            this.selectedVariants.set(variantId, data);

            // Mettre à jour l'UI
            const input = document.querySelector(`input[data-variant-id="${variantId}"]`);
            if (input) {
              input.value = data.quantity;
              const card = input.closest('.flavor-card-addon');
              if (card && data.quantity > 0) {
                card.classList.add('flavor-card-addon--selected');
                // Mettre à jour l'affichage de la quantité
                const quantityDisplay = card.querySelector('.flavor-card-addon__quantity-display');
                if (quantityDisplay) {
                  quantityDisplay.textContent = data.quantity;
                  quantityDisplay.style.display = 'flex';
                }
              }
            }
          });

          // Mettre à jour le prix du bouton
          if (this.selectedVariants.size > 0) {
            this.updateButtonPrice();
          }
        } catch (e) {
          console.error('Erreur restauration localStorage:', e);
        }
      }

      interceptFetchRequests() {
        const originalFetch = window.fetch;
        const self = this;
        
        window.fetch = function(...args) {
          const [url, options] = args;
          
          if (url && (url.includes('/cart/add') || url.includes('cart_add_url'))) {
            // Détecter les requêtes upsell (id simple SANS items[])
            let isUpsellRequest = false;
            
            if (options && options.body instanceof FormData) {
              const formData = options.body;
              const hasSimpleId = formData.has('id');
              const hasItemsArray = Array.from(formData.keys()).some(key => key.startsWith('items['));
              
              if (hasSimpleId && !hasItemsArray) {
                isUpsellRequest = true;
              }
            }
            
            // Modifier uniquement les requêtes flavor-cards
            if (!isUpsellRequest && self.displayMode === 'products' && self.selectedVariants.size > 0) {
              if (options && options.body instanceof FormData) {
                const formData = options.body;
                const sections = formData.get('sections');
                const sections_url = formData.get('sections_url');
                
                const newFormData = new FormData();
                
                let itemIndex = 0;
                self.selectedVariants.forEach((data, variantId) => {
                  newFormData.append(`items[${itemIndex}][id]`, variantId);
                  newFormData.append(`items[${itemIndex}][quantity]`, data.quantity);
                  itemIndex++;
                });
                
                if (sections) newFormData.append('sections', sections);
                if (sections_url) newFormData.append('sections_url', sections_url);
                
                options.body = newFormData;
              }
            }
            
            const fetchPromise = originalFetch.apply(this, args);
            
            // Reset après succès (seulement pour flavor-cards)
            if (!isUpsellRequest && self.selectedVariants.size > 0) {
              return fetchPromise.then(response => {
                if (response.ok) {
                  setTimeout(() => {
                    self.clearLocalStorage();
                    self.reset();
                  }, 800);
                }
                return response;
              }).catch(error => {
                console.error('[FlavorCards] Erreur:', error);
                throw error;
              });
            }
            
            return fetchPromise;
          }
          
          return originalFetch.apply(this, args);
        };
      }
  
      interceptFormSubmit() {
        if (!this.productForm) return;
        
        // Dawn utilise un custom element <product-form> qui gère le submit
        // Cherchons d'abord dans tout le document
        let productFormElement = document.querySelector('product-form');
        
        if (!productFormElement) {
          const submitButton = document.querySelector(`button[form="${this.productForm.id}"]`);
          if (submitButton) {
            productFormElement = submitButton.closest('product-form');
          }
        }
        
        if (!productFormElement) {
          return;
        }
        
        // Attendre que le custom element soit entièrement initialisé
        const tryInterceptSubmit = () => {
          if (!productFormElement.onSubmitHandler) {
            setTimeout(tryInterceptSubmit, 100);
            return;
          }
          
          // Sauvegarder la méthode originale
          const originalOnSubmitHandler = productFormElement.onSubmitHandler.bind(productFormElement);
          
          // Remplacer par notre méthode
          productFormElement.onSubmitHandler = (evt) => {
            // En mode produits avec des sélections, utiliser notre API
            if (this.displayMode === 'products' && this.selectedVariants.size > 0) {
              evt.preventDefault();
              this.addToCartViaAPI();
              return;
            }
            
            // Sinon, utiliser le comportement par défaut de Dawn
            originalOnSubmitHandler(evt);
          };
        };
        
        tryInterceptSubmit();
      }
  
      async addToCartViaAPI() {
        // Préparer les items pour l'API Cart
        const items = [];
        this.selectedVariants.forEach((data, variantId) => {
          items.push({
            id: variantId,
            quantity: data.quantity
          });
        });
        
        // Désactiver le bouton pendant le traitement
        const submitButton = this.productForm.querySelector('button[type="submit"]');
        const originalText = submitButton.querySelector('span').textContent;
        submitButton.disabled = true;
        submitButton.querySelector('span').textContent = 'Ajout en cours...';
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const result = await response.json();
          
          // Afficher un message de succès
          submitButton.querySelector('span').textContent = '✓ Ajouté !';
          
          // Mettre à jour le compteur du panier et ouvrir le drawer
          this.updateCartUI(result);

          // Reset : Vider les sélections et le localStorage après ajout au panier
          setTimeout(() => {
            this.clearLocalStorage();
            this.reset();
          }, 1000);
          
          // Réactiver le bouton après 2 secondes
          setTimeout(() => {
            submitButton.disabled = false;
            submitButton.querySelector('span').textContent = originalText;
          }, 2000);
          
        } catch (error) {
          console.error('Erreur ajout au panier:', error);
          alert('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
          submitButton.disabled = false;
          submitButton.querySelector('span').textContent = originalText;
        }
      }
  
      updateCartUI(cartData) {
        // Mettre à jour le compteur du panier (bubble)
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            // Mettre à jour le compteur
            const cartCount = document.querySelector('cart-notification-button .cart-count-bubble span, #cart-icon-bubble span');
            if (cartCount) {
              cartCount.textContent = cart.item_count;
            }
            
            // Ouvrir le cart drawer si disponible
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              const cartNotification = document.querySelector('cart-notification');
              if (cartNotification && typeof cartNotification.open === 'function') {
                cartNotification.open();
              }
            }
          })
          .catch(error => {
            // Silently fail
          });
      }
  
      loadConfiguration() {
        // Charger la configuration depuis les data attributes du container
        const config = this.container.dataset;

        if (config.tiers) {
          try {
            this.tiers = JSON.parse(config.tiers);
          } catch (e) {
            // Use defaults
          }
        }

        if (config.gifts) {
          try {
            this.gifts = JSON.parse(config.gifts);
          } catch (e) {
            // Use defaults
          }
        }

        if (config.discounts) {
          try {
            this.discounts = JSON.parse(config.discounts);
          } catch (e) {
            // Use defaults
          }
        }

        // Configuration du palier 5 (caché)
        if (config.tier5Enabled !== undefined) {
          this.tier5Enabled = config.tier5Enabled === 'true';
        }
        
        if (config.tier5Qty) {
          this.tier5Qty = parseInt(config.tier5Qty) || 5;
        }

        // Seuil de livraison offerte (en centimes). Le badge "Livraison offerte"
        // ne s'active que lorsque le total du bundle atteint ce montant.
        // 0 = pas de seuil montant (badge basé uniquement sur la quantité, ancien comportement).
        this.freeShippingThreshold = parseInt(config.freeShippingThreshold) || 0;

        // Configuration de la remise globale
        this.globalDiscountEnabled = config.globalDiscountEnabled === 'true';
        this.globalDiscountPercentage = parseFloat(config.globalDiscountPercentage) || 0;
        this.globalDiscountColor = config.globalDiscountColor || '#dc2626';

        // Appliquer la remise globale si activée
        if (this.globalDiscountEnabled && this.globalDiscountPercentage > 0) {
          this.applyGlobalDiscountToPrices();
          
          // Ajouter la classe pour ajuster la position de la ligne
          if (this.quantityMeter) {
            this.quantityMeter.classList.add('quantity-meter-addon--global-discount');
          }
        } else {
          // Retirer la classe si la remise est désactivée
          if (this.quantityMeter) {
            this.quantityMeter.classList.remove('quantity-meter-addon--global-discount');
          }
        }
      }

      applyGlobalDiscountToPrices() {
        // Appliquer la remise globale visuelle sur les prix du quantity meter
        const priceElements = this.container.querySelectorAll('.quantity-meter-addon-item__price[data-original-price]');
        
        priceElements.forEach(priceEl => {
          const originalPriceText = priceEl.dataset.originalPrice;
          
          // Regex flexible pour extraire le prix avec ou sans décimales
          // Formats supportés: "25€", "25€/unité", "25,00€", "25.00€", "$25", "25 €"
          const priceRegex = /(\d+(?:[.,]\d+)?)\s*([€$£])/;
          const match = originalPriceText.match(priceRegex);
          
          if (match) {
            const originalPrice = parseFloat(match[1].replace(',', '.'));
            const currency = match[2];
            const suffix = originalPriceText.substring(match.index + match[0].length);
            
            // Calculer le prix avec remise
            const discountedPrice = originalPrice * (1 - this.globalDiscountPercentage / 100);
            
            // Formater le prix avec le bon nombre de décimales
            let formattedDiscountedPrice;
            if (originalPriceText.includes(',') || originalPriceText.includes('.')) {
              // Le prix original a des décimales, on garde 2 décimales
              formattedDiscountedPrice = discountedPrice.toFixed(2).replace('.', ',');
            } else {
              // Pas de décimales dans l'original
              formattedDiscountedPrice = Math.round(discountedPrice).toString();
            }
            
            // Construire le HTML avec prix barré et prix soldé
            priceEl.innerHTML = `
              <span class="quantity-meter-addon-item__price--original">${originalPriceText}</span>
              <span class="quantity-meter-addon-item__price--discounted" style="color: ${this.globalDiscountColor};">
                ${formattedDiscountedPrice}${currency}${suffix}
              </span>
            `;
          }
        });
      }
  
      bindEvents() {
        // Quantity input events
        this.quantityInputs.forEach(input => {
          const quantityContainer = input.closest('.flavor-card-addon__quantity-controls');
          const plusButton = quantityContainer.querySelector('[name="plus"]');
          const minusButton = quantityContainer.querySelector('[name="minus"]');
          
          // Button clicks
          plusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.incrementQuantity(input);
          });
          
          minusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.decrementQuantity(input);
          });
        });
  
        // Add button events
        const addButtons = this.container.querySelectorAll('.flavor-card-addon__add-button');
        addButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleAddButtonClick(button);
          });
        });
  
        // Card click events
        this.cards.forEach(card => {
          card.addEventListener('click', (e) => {
            // Ne pas intercepter les clics sur les contrôles de quantité, les boutons ou les liens
            if (e.target.closest('.flavor-card-addon__quantity-controls')) return;
            if (e.target.closest('.flavor-card-addon__add-button')) return;
            if (e.target.closest('a')) return; // Laisser les liens fonctionner normalement
            this.handleCardClick(card);
          });
        });
      }
  
      handleQuantityChangeDirectly(input, quantity) {
        const variantId = input.dataset.variantId;
        const card = input.closest('.flavor-card-addon');

        // Validate quantity
        const max = parseInt(input.dataset.max) || 999;

        if (quantity < 0) {
          input.value = 0;
          quantity = 0;
        }

        if (quantity > max) {
          input.value = max;
          quantity = max;
        }

        // Update selected variants
        if (quantity > 0) {
          const compareAtPrice = card.dataset.variantCompareAtPrice;
          this.selectedVariants.set(variantId, {
            quantity: quantity,
            price: parseInt(card.dataset.variantPrice),
            compare_at_price: compareAtPrice ? parseInt(compareAtPrice) : null,
            title: card.querySelector('.flavor-card-addon__title').textContent.trim()
          });
          card.classList.add('flavor-card-addon--selected');
        } else {
          this.selectedVariants.delete(variantId);
          card.classList.remove('flavor-card-addon--selected');
        }
        this.toggleQuantityDisplay(card, quantity);
        this.updateQuantityButtons(input);
        this.updateQuantityMeter();
        this.updateProductForm();
        this.updateSummary();
        this.saveToLocalStorage(); // Sauvegarder après chaque modification
      }
  
      incrementQuantity(input) {
        const currentValue = parseInt(input.value) || 0;
        const max = parseInt(input.dataset.max) || 999;
        
        if (currentValue < max) {
          const newValue = currentValue + 1;
          input.value = newValue;
          this.handleQuantityChangeDirectly(input, newValue);
        }
      }
  
      decrementQuantity(input) {
        const currentValue = parseInt(input.value) || 0;
        
        if (currentValue > 0) {
          const newValue = currentValue - 1;
          input.value = newValue;
          this.handleQuantityChangeDirectly(input, newValue);
        }
      }
  
      updateQuantityButtons(input) {
        const quantityContainer = input.closest('.flavor-card-addon__quantity-controls');
        const plusButton = quantityContainer.querySelector('[name="plus"]');
        const minusButton = quantityContainer.querySelector('[name="minus"]');
        
        const value = parseInt(input.value) || 0;
        const max = parseInt(input.dataset.max) || 999;
        
        const shouldDisableMinus = value <= 0;
        const shouldDisablePlus = value >= max;
        
        minusButton.disabled = shouldDisableMinus;
        plusButton.disabled = shouldDisablePlus;
        
        minusButton.classList.toggle('disabled', shouldDisableMinus);
        plusButton.classList.toggle('disabled', shouldDisablePlus);
      }
  
      handleCardClick(card) {
        const input = card.querySelector('.flavor-card-addon__quantity-input');
        if (!input || card.classList.contains('flavor-card-addon--unavailable')) return;
        
        const currentQuantity = parseInt(input.value) || 0;
        if (currentQuantity === 0) {
          this.incrementQuantity(input);
        }
      }
  
      initializeQuantityButtons() {
        this.quantityInputs.forEach(input => {
          this.updateQuantityButtons(input);
        });
      }

      initializeAddButtons() {
        // Initialiser l'affichage correct pour chaque carte
        this.cards.forEach(card => {
          const input = card.querySelector('.flavor-card-addon__quantity-input');
          if (input) {
            const quantity = parseInt(input.value) || 0;
            this.toggleQuantityDisplay(card, quantity);
          }
        });
      }

      toggleQuantityDisplay(card, quantity) {
        const addButton = card.querySelector('.flavor-card-addon__add-button');
        const quantityControls = card.querySelector('.flavor-card-addon__quantity-controls');
        
        if (!addButton || !quantityControls) return;
        
        if (quantity === 0) {
          // Afficher le bouton "Ajouter"
          addButton.style.display = 'inline-flex';
          quantityControls.style.display = 'none';
        } else {
          // Afficher le sélecteur de quantité
          addButton.style.display = 'none';
          quantityControls.style.display = 'flex';
        }
      }

      handleAddButtonClick(button) {
        const variantId = button.dataset.variantId;
        const card = button.closest('.flavor-card-addon');
        const input = card.querySelector(`.flavor-card-addon__quantity-input[data-variant-id="${variantId}"]`);
        
        if (input && !card.classList.contains('flavor-card-addon--unavailable')) {
          // Mettre la quantité à 1
          input.value = 1;
          this.handleQuantityChangeDirectly(input, 1);
        }
      }
  
      updateQuantityMeter() {
        if (!this.quantityMeter) return;
        
        // Calculer la quantité totale
        let totalQuantity = 0;
        this.selectedVariants.forEach(data => {
          totalQuantity += data.quantity;
        });
        
        // Mettre à jour le badge livraison offerte
        const shippingBadge = this.quantityMeter.querySelector('[data-shipping-badge]');
        if (shippingBadge) {
          let badgeActif;

          if (this.freeShippingThreshold > 0) {
            // Basé sur le MONTANT réel du bundle (prix après remise du palier).
            // Le badge n'apparaît que si la livraison est réellement offerte.
            let tier = '1';
            if (this.tier5Enabled && totalQuantity >= this.tier5Qty) {
              tier = '5';
            } else if (totalQuantity >= 4) {
              tier = '4';
            } else if (totalQuantity >= 3) {
              tier = '3';
            } else if (totalQuantity >= 2) {
              tier = '2';
            }

            let total = 0;
            this.selectedVariants.forEach((data) => {
              total += (data.price * data.quantity);
            });

            const discount = this.discounts[tier];
            if (discount && discount.type !== 'none') {
              if (discount.type === 'percentage') {
                total = total * (1 - discount.value / 100);
              } else if (discount.type === 'fixed') {
                total = total - (discount.value * 100);
              }
            }
            total = Math.ceil(total);

            badgeActif = total >= this.freeShippingThreshold;
          } else {
            // Ancien comportement : basé sur la quantité (fallback si aucun seuil montant).
            badgeActif = totalQuantity >= 2;
          }

          if (badgeActif) {
            shippingBadge.classList.add('active');
            shippingBadge.classList.add('visible');
          } else {
            shippingBadge.classList.remove('active');
            shippingBadge.classList.remove('visible');
          }
        }
        
        // Mettre à jour la barre de progression
        const progressLine = this.quantityMeter.querySelector('[data-progress-line-addon]');
        const items = this.quantityMeter.querySelectorAll('.quantity-meter-addon-item');
        
        // Calculer le pourcentage de progression
        let progress = 0;
        let currentTier = 0;
        let lastActiveTier = -1;
        
        // D'abord, traiter tous les cercles pour déterminer le dernier actif
        for (let i = 0; i < this.tiers.length; i++) {
          const tier = this.tiers[i];
          const circle = items[i] ? items[i].querySelector('.quantity-meter-addon-item__circle') : null;
          const circleInner = items[i] ? items[i].querySelector('.quantity-meter-addon-item__circle-inner') : null;
          const estAtteint = totalQuantity >= tier.qty;
          
          if (estAtteint) {
            if (circle) circle.classList.add('filled');
            if (circleInner) circleInner.classList.add('filled');
            lastActiveTier = i;
            currentTier = i;
            progress = ((i + 1) / this.tiers.length) * 100;
          } else {
            if (circle) circle.classList.remove('filled');
            if (circleInner) circleInner.classList.remove('filled');
          }
        }
        
        // Ensuite, traiter les prix : seul le dernier cercle actif a un prix actif
        for (let i = 0; i < this.tiers.length; i++) {
          const priceElement = items[i] ? items[i].querySelector('.quantity-meter-addon-item__price') : null;
          const estPrixActif = (i === lastActiveTier);
          
          if (estPrixActif) {
            if (priceElement) priceElement.classList.remove('subdued');
          } else {
            if (priceElement) priceElement.classList.add('subdued');
          }
        }
        
        // Mettre à jour la barre de progression
        if (items.length > 0) {
          const firstCircle = items[0].querySelector('.quantity-meter-addon-item__circle');
          const lastCircle = items[items.length - 1].querySelector('.quantity-meter-addon-item__circle');
          const activeCircle = items[currentTier].querySelector('.quantity-meter-addon-item__circle');

          if (firstCircle && lastCircle && activeCircle) {
            const CORRECTION = 10;
            
            const firstCenter = firstCircle.offsetLeft + firstCircle.offsetWidth / 2 + CORRECTION;
            const lastCenter = lastCircle.offsetLeft + lastCircle.offsetWidth / 2 + CORRECTION;
            const activeCenter = activeCircle.offsetLeft + activeCircle.offsetWidth / 2 + CORRECTION;

            // Barre de fond
            const bgLine = this.quantityMeter.querySelector('.quantity-meter-addon-line');
            if (bgLine) {
              bgLine.style.left = firstCenter + 'px';
              bgLine.style.width = (lastCenter - firstCenter) + 'px';
            }

            // Barre active
            if (progressLine) {
              if (currentTier > 0) {
                progressLine.style.left = firstCenter + 'px';
                progressLine.style.width = (activeCenter - firstCenter) + 'px';
              } else {
                progressLine.style.left = firstCenter + 'px';
                progressLine.style.width = '0';
              }
            }
          }
        }
        
        // Mettre à jour les cadeaux
        this.updateGifts(totalQuantity);
      }
      
      updateGifts(totalQuantity) {
        // Envoyer un événement global pour les blocs gift-system indépendants
        const event = new CustomEvent('flavorCardsQuantityChanged', {
          detail: { 
            totalQuantity: totalQuantity,
            selectedVariants: Array.from(this.selectedVariants.entries())
          }
        });
        document.dispatchEvent(event);
      }
  
      updateProductForm() {
        if (!this.productForm) return;
        
        // Ne jamais modifier les formulaires upsell du cart drawer
        if (this.productForm.closest('cart-drawer') || 
            this.productForm.closest('#CartDrawer') ||
            this.productForm.classList.contains('upsell-drawer-form')) {
          return;
        }
        
        // Remove existing flavor cards inputs
        const existingInputs = this.productForm.querySelectorAll('input[data-flavor-cards-addon-input]');
        existingInputs.forEach(input => input.remove());
        
        // Always hide the main inputs when using flavor cards
        const mainVariantInput = this.productForm.querySelector('input[name="id"]');
        const mainQuantityInput = this.productForm.querySelector('input[name="quantity"]');
        
        if (mainVariantInput) {
          mainVariantInput.style.display = 'none';
          mainVariantInput.disabled = true;
        }
        if (mainQuantityInput) {
          mainQuantityInput.style.display = 'none';
          mainQuantityInput.disabled = true;
        }
        
        // If we have selected variants, add them as items array
        if (this.selectedVariants.size > 0) {
          let itemIndex = 0;
          this.selectedVariants.forEach((data, variantId) => {
            const idInput = document.createElement('input');
            idInput.type = 'hidden';
            idInput.name = `items[${itemIndex}][id]`;
            idInput.value = variantId;
            idInput.setAttribute('data-flavor-cards-addon-input', 'true');
            this.productForm.appendChild(idInput);
            
            const quantityInput = document.createElement('input');
            quantityInput.type = 'hidden';
            quantityInput.name = `items[${itemIndex}][quantity]`;
            quantityInput.value = data.quantity;
            quantityInput.setAttribute('data-flavor-cards-addon-input', 'true');
            this.productForm.appendChild(quantityInput);
            
            itemIndex++;
          });
        } else {
          // If no selections, add a dummy input to prevent default behavior
          const dummyInput = document.createElement('input');
          dummyInput.type = 'hidden';
          dummyInput.name = 'items[0][id]';
          dummyInput.value = '';
          dummyInput.setAttribute('data-flavor-cards-addon-input', 'true');
          this.productForm.appendChild(dummyInput);
          
          const dummyQuantityInput = document.createElement('input');
          dummyQuantityInput.type = 'hidden';
          dummyQuantityInput.name = 'items[0][quantity]';
          dummyQuantityInput.value = '0';
          dummyQuantityInput.setAttribute('data-flavor-cards-addon-input', 'true');
          this.productForm.appendChild(dummyQuantityInput);
        }
        
        // Mise à jour du prix du bouton
        this.updateButtonPrice();
      }
  
      updateButtonPrice() {
        if (!this.selectedVariants) {
          return;
        }

        // Calculer la quantité totale pour déterminer le palier de discount
        let totalQuantity = 0;
        this.selectedVariants.forEach((data) => {
          totalQuantity += data.quantity;
        });

        // Déterminer le palier actif (1, 2, 3, 4 ou 5)
        let activeTier = '1';

        // Vérifier d'abord le palier 5 si activé
        if (this.tier5Enabled && totalQuantity >= this.tier5Qty) {
          activeTier = '5';
        } else if (totalQuantity >= 4) {
          activeTier = '4';
        } else if (totalQuantity >= 3) {
          activeTier = '3';
        } else if (totalQuantity >= 2) {
          activeTier = '2';
        }

        // Calculer le prix total SANS discount
        let totalPriceBeforeDiscount = 0;
        this.selectedVariants.forEach((data) => {
          totalPriceBeforeDiscount += (data.price * data.quantity);
        });

        // Calculer le compare_at_price total (prix original avant toutes remises)
        let totalCompareAtPrice = 0;
        this.selectedVariants.forEach((data) => {
          // Si la variante a un compare_at_price, l'utiliser, sinon utiliser le prix actuel
          const comparePrice = data.compare_at_price || data.price;
          totalCompareAtPrice += (comparePrice * data.quantity);
        });

        // Appliquer le discount sur le TOTAL selon le palier actif
        let totalPrice = totalPriceBeforeDiscount;
        const discount = this.discounts[activeTier];

        if (discount && discount.type !== 'none') {
          if (discount.type === 'percentage') {
            const reductionMultiplier = (1 - discount.value / 100);
            totalPrice = totalPriceBeforeDiscount * reductionMultiplier;
          } else if (discount.type === 'fixed') {
            const discountInCentimes = discount.value * 100;
            totalPrice = totalPriceBeforeDiscount - discountInCentimes;
          }
        }

        // Arrondir au centime supérieur (évite les erreurs de précision flottante)
        totalPrice = Math.ceil(totalPrice);

        // TOUJOURS dispatcher l'event, même si ButtonPriceManager n'existe pas encore
        const event = new CustomEvent('flavorCardsPrice:change', {
          detail: {
            totalPrice: totalPrice, // Prix avec les remises par palier uniquement
            totalPriceBeforeDiscount: totalPriceBeforeDiscount,
            totalCompareAtPrice: totalCompareAtPrice, // Prix original avant toutes remises
            selectedVariants: Array.from(this.selectedVariants.entries()),
            hasSelections: this.selectedVariants.size > 0,
            activeTier: activeTier,
            totalQuantity: totalQuantity,
            discount: discount,
            globalDiscountApplied: false // La remise globale n'affecte plus le prix du bouton
          }
        });
        document.dispatchEvent(event);
      }
  
      updateSummary() {
        const summaryContainer = this.container.querySelector('.flavor-cards-addon__summary');
        if (!summaryContainer) return;

        const summaryContent = summaryContainer.querySelector('.flavor-cards-addon__summary-content');
        if (!summaryContent) return;

        // Clear existing content
        summaryContent.innerHTML = '';

        if (this.selectedVariants.size === 0) {
          summaryContainer.classList.remove('has-selections');
          return;
        }

        summaryContainer.classList.add('has-selections');

        // Calculer la quantité totale pour déterminer le palier
        let totalQuantity = 0;
        this.selectedVariants.forEach((data) => {
          totalQuantity += data.quantity;
        });

        // Déterminer le palier actif (1, 2, 3, 4 ou 5)
        let activeTier = '1';
        
        // Vérifier d'abord le palier 5 si activé
        if (this.tier5Enabled && totalQuantity >= this.tier5Qty) {
          activeTier = '5';
        } else if (totalQuantity >= 4) {
          activeTier = '4';
        } else if (totalQuantity >= 3) {
          activeTier = '3';
        } else if (totalQuantity >= 2) {
          activeTier = '2';
        }

        // Calculer le total SANS discount
        let totalPriceBeforeDiscount = 0;

        // Add each selected variant (prix de base)
        this.selectedVariants.forEach((data, variantId) => {
          const itemTotal = (data.price * data.quantity) / 100;
          totalPriceBeforeDiscount += itemTotal;

          const summaryItem = document.createElement('div');
          summaryItem.className = 'flavor-cards-addon__summary-item';
          summaryItem.innerHTML = `
            <span>${data.title} × ${data.quantity}</span>
            <span>€${itemTotal.toFixed(2)}</span>
          `;
          summaryContent.appendChild(summaryItem);
        });

        // Appliquer le discount sur le total
        let totalPrice = totalPriceBeforeDiscount;
        const discount = this.discounts[activeTier];

        if (discount && discount.type !== 'none') {
          if (discount.type === 'percentage') {
            totalPrice = totalPriceBeforeDiscount * (1 - discount.value / 100);
          } else if (discount.type === 'fixed') {
            totalPrice = totalPriceBeforeDiscount - discount.value;
          }

          // Afficher la ligne de discount
          const discountItem = document.createElement('div');
          discountItem.className = 'flavor-cards-addon__summary-item flavor-cards-addon__summary-discount';
          const discountAmount = totalPriceBeforeDiscount - totalPrice;
          discountItem.innerHTML = `
            <span>Discount (${discount.type === 'percentage' ? discount.value + '%' : '€' + discount.value})</span>
            <span>-€${discountAmount.toFixed(2)}</span>
          `;
          summaryContent.appendChild(discountItem);
        }

        // Add total
        const totalItem = document.createElement('div');
        totalItem.className = 'flavor-cards-addon__summary-total';
        totalItem.innerHTML = `
          <span>Total (${totalQuantity} items)</span>
          <span>€${totalPrice.toFixed(2)}</span>
        `;
        summaryContent.appendChild(totalItem);
      }
  
      // Public methods for external integration
      getSelectedVariants() {
        return Array.from(this.selectedVariants.entries());
      }
  
      getTotalQuantity() {
        let total = 0;
        this.selectedVariants.forEach(data => {
          total += data.quantity;
        });
        return total;
      }
  
      getTotalPrice() {
        let total = 0;
        this.selectedVariants.forEach(data => {
          total += (data.price * data.quantity);
        });
        return total;
      }
  
  reset() {
    this.selectedVariants.clear();
    
    // D'abord tout mettre à 0
    this.quantityInputs.forEach(input => {
      input.value = 0;
      this.updateQuantityButtons(input);
    });
    this.cards.forEach(card => {
      card.classList.remove('flavor-card-addon--selected');
      this.toggleQuantityDisplay(card, 0);
    });
    
    // Ensuite réappliquer les quantités par défaut
    this.quantityInputs.forEach(input => {
      const defaultQuantity = parseInt(input.dataset.defaultQuantity) || 0;
      
      if (defaultQuantity > 0) {
        input.value = defaultQuantity;
        this.handleQuantityChangeDirectly(input, defaultQuantity);
      }
    });
    
    this.updateQuantityMeter();
    this.updateProductForm();
    this.updateSummary();
  }      initializeScrollIndicator() {
        const scrollWrapper = this.container.querySelector('.flavor-cards-addon-scroll-wrapper');
        if (!scrollWrapper) return;

        // Vérifier si le contenu nécessite un scroll
        const checkScrollNeeded = () => {
          const hasScroll = scrollWrapper.scrollHeight > scrollWrapper.clientHeight;
          if (!hasScroll) {
            scrollWrapper.classList.add('no-scroll');
          } else {
            scrollWrapper.classList.remove('no-scroll');
          }
        };

        // Gérer l'état du scroll (masquer l'indicateur en bas)
        const handleScroll = () => {
          const scrollTop = scrollWrapper.scrollTop;
          const scrollHeight = scrollWrapper.scrollHeight;
          const clientHeight = scrollWrapper.clientHeight;
          
          // Considérer qu'on est en bas si on est à moins de 10px du bas
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
          
          if (isAtBottom) {
            scrollWrapper.classList.add('scrolled-to-bottom');
          } else {
            scrollWrapper.classList.remove('scrolled-to-bottom');
          }
        };

        // Vérifier au chargement
        checkScrollNeeded();
        handleScroll();

        // Écouter les événements de scroll
        scrollWrapper.addEventListener('scroll', handleScroll);

        // Re-vérifier après un court délai (pour les images qui se chargent)
        setTimeout(() => {
          checkScrollNeeded();
          handleScroll();
        }, 500);

        // Re-vérifier lors du redimensionnement de la fenêtre
        window.addEventListener('resize', () => {
          checkScrollNeeded();
          handleScroll();
        });
      }
    }
  
    // Auto-initialize when DOM is ready
    function initializeFlavorCards() {
      const containers = document.querySelectorAll('[data-flavor-cards-addon]');
      containers.forEach(container => {
        if (!container.flavorCardsAddon) {
          container.flavorCardsAddon = new FlavorCardsAddon(container);
        }
      });
    }
  
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeFlavorCards);
    } else {
      initializeFlavorCards();
    }
  
    // Re-initialize on theme section loads (for theme editor)
    document.addEventListener('shopify:section:load', initializeFlavorCards);
  
    // Export for global access
    window.FlavorCardsAddon = FlavorCardsAddon;
  
  })();
  